import { FormHelperText } from 'material-ui';
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Select from 'react-select';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import NoSsr from '@material-ui/core/NoSsr';
import TextField from '@material-ui/core/TextField';
import Paper from '@material-ui/core/Paper';
import Chip from '@material-ui/core/Chip';
import MenuItem from '@material-ui/core/MenuItem';
import CancelIcon from '@material-ui/icons/Cancel';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import { Field } from 'redux-form';

const ITEM_HEIGHT = 48;
const styles = theme => ({
    root: {
        display: 'block',
    },
    chip: {
        margin: theme.spacing.unit / 4,
    },
    error: {
        display: 'flex',
        justifyContent: 'center',
        margin: '8px 0 0',
        fontSize: '0.75rem',
        color: 'red',
    },
    // We had to use a lot of global selectors in order to style react-select.
    // We are waiting on https://github.com/JedWatson/react-select/issues/1679
    // to provide a much better implementation.
    // Also, we had to reset the default style injected by the library.
    '@global': {
        '.Select-underline': {
            left: 0,
            right: 0,
            bottom: 0,
            height: 1,
            content: '',
            position: 'absolute',
            pointerEvents: 'none',
            backgroundColor: 'rgba(0, 0, 0, 0.42)',
        },
        '.Select-underline-after': {
            left: 0,
            right: 0,
            bottom: 0,
            height: 1,
            content: '',
            position: 'absolute',
            pointerEvents: 'none',
            backgroundColor: '#1848a5',
            transform: 'scaleX(0)',
            transition: 'transform 500ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
        },
        '.Select-container:hover > .Select-underline-after': {
            height: 2,
            backgroundColor: 'rgba(0, 0, 0, 0.87)',
            transform: 'scaleX(1)',
            transition: 'transform 0ms cubic-bezier(0.0, 0, 0.0, 1) 0ms',
        },
        '.Select.is-focused + .Select-underline + .Select-underline-after': {
            height: 2,
            backgroundColor: '#1848a5',
            transform: 'scaleX(1)',
        },
        '.Select-control': {
            display: 'flex',
            alignItems: 'center',
            border: 0,
            background: 'transparent',
            '&:hover': {
                boxShadow: 'none',
            },
        },
        '.Select-multi-value-wrapper': {
            flexGrow: 1,
            display: 'flex',
            flexWrap: 'wrap',
        },
        '.Select--multi .Select-input': {
            margin: 0,
        },
        '.Select': {
            marginTop: 16
        },
        '.Select.is-focused': {
            borderRadius: 0,
            background: 'rgba(0, 0, 0, 0.05)'
        },
        '.Select.has-value.is-clearable.Select--single > .Select-control .Select-value': {
            padding: 0,
        },
        '.Select.has-value.Select--single > .Select-control .Select-value .Select-value-label, .Select.has-value.is-pseudo-focused.Select--single > .Select-control .Select-value .Select-value-label': {
            color: '#333',
            width: 'calc(100% - 24px)',
        },
        '.Select-noresults': {
            padding: theme.spacing.unit * 2,
        },
        '.Select-input': {
            display: 'inline-flex !important',
            padding: 0,
            height: 'auto',
        },
        '.Select-input input': {
            background: 'transparent',
            border: 0,
            padding: 0,
            cursor: 'default',
            display: 'inline-block',
            fontFamily: 'inherit',
            fontSize: 'inherit',
            margin: 0,
            outline: 0
        },
        '.Select-placeholder, .Select--single .Select-value': {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.pxToRem(16),
            padding: 0
        },
        '.Select-container.error .Select-placeholder': {
            color: 'red',
        },
        '.Select-placeholder': {
            color: 'rgb(188, 188, 188)',
        },
        '.Select-menu-outer': {
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[2],
            position: 'absolute',
            left: 0,
            top: `calc(100% + ${theme.spacing.unit}px)`,
            width: '100%',
            zIndex: 2,
            maxHeight: ITEM_HEIGHT * 4.5,
        },
        '.Select.is-focused:not(.is-open) > .Select-control': {
            boxShadow: 'none',
        },
        '.Select-menu': {
            maxHeight: ITEM_HEIGHT * 4.5,
            overflowY: 'auto'
        },
        '.Select-menu div': {
            boxSizing: 'content-box',
        },
        '.Select-arrow-zone, .Select-clear-zone': {
            color: theme.palette.action.active,
            cursor: 'pointer',
            height: 21,
            width: 21,
            zIndex: 1,
        },
        // Only for screen readers. We can't use display none.
        '.Select-aria-only': {
            position: 'absolute',
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            height: 1,
            width: 1,
            margin: -1,
        },
    },
});

function NoOptionsMessage(props) {
    return (
        <Typography
            color="textSecondary"
            className={props.selectProps.classes.noOptionsMessage}
            {...props.innerProps}
        >
            {props.children}
        </Typography>
    );
}

function inputComponent({ inputRef, ...props }) {
    return <div ref={inputRef} {...props} />;
}

function Control(props) {
    return (
        <TextField
            fullWidth
            InputProps={{
                inputComponent,
                inputProps: {
                    className: props.selectProps.classes.input,
                    inputRef: props.innerRef,
                    children: props.children,
                    ...props.innerProps,
                },
            }}
            {...props.selectProps.textFieldProps}
        />
    );
}

function Option(props) {
    return (
        <MenuItem
            buttonRef={props.innerRef}
            selected={props.isFocused}
            component="div"
            style={{
                fontWeight: props.isSelected ? 500 : 400,
            }}
            {...props.innerProps}
        >
            {props.children}
        </MenuItem>
    );
}

function Placeholder(props) {
    return (
        <Typography
            color="textSecondary"
            className={props.selectProps.classes.placeholder}
            {...props.innerProps}
        >
            {props.children}
        </Typography>
    );
}

function SingleValue(props) {
    return (
        <Typography className={props.selectProps.classes.singleValue} {...props.innerProps}>
            {props.children}
        </Typography>
    );
}

function ValueContainer(props) {
    return <div className={props.selectProps.classes.valueContainer}>{props.children}</div>;
}

function MultiValue(props) {
    return (
        <Chip
            tabIndex={-1}
            label={props.children}
            className={classNames(props.selectProps.classes.chip, {
                [props.selectProps.classes.chipFocused]: props.isFocused,
            })}
            onDelete={props.removeProps.onClick}
            deleteIcon={<CancelIcon {...props.removeProps} />}
        />
    );
}

function Menu(props) {
    return (
        <Paper square className={props.selectProps.classes.paper} {...props.innerProps}>
            {props.children}
        </Paper>
    );
}

const components = {
    Control,
    Menu,
    MultiValue,
    NoOptionsMessage,
    Option,
    Placeholder,
    SingleValue,
    ValueContainer,
};

class ReactSelect extends React.Component {
    handleChange = (name, callback) => (value) => {
        this.setState({
            [name]: value,
        });

        if (callback) callback(value);
    };

    handleBlur = (name, callback) => (value) => {
        if (callback) callback(value);
    };

    render() {
        const {
            classes,
            label,
            name,
            options,
            onBlur,
            onChange,
            placeholder,
            style,
            value
        } = this.props;

        const Selector = ({ meta: { touched, error } }) => (
            <div className={classes.root}>
                <FormControl className={(touched && error) ? 'Select-container error' : 'Select-container'}>
                    <NoSsr>
                        {
                            value
                                ? <InputLabel
                                    htmlFor={`${name}-selector`}
                                    shrink
                                    style={(error) ? { color: 'red' } : { color: 'rgb(188, 188, 188)' }}
                                >
                                    {label}
                                  </InputLabel>
                                : null
                        }
                        <Select
                            name={name}
                            classes={classes}
                            options={options}
                            components={components}
                            value={value}
                            placeholder={placeholder}
                            style={style}
                            onChange={this.handleChange(name, onChange)}
                            onBlur={this.handleBlur(name, onBlur)}
                            isClearable
                        />
                        <span className="Select-underline" />
                        <span className="Select-underline-after" />
                    </NoSsr>
                </FormControl>
                {
                    touched && error &&
                    <FormHelperText className={classes.error}>{error}</FormHelperText>
                }
            </div>
        );

        return (
            // wrap react-select in a redux field here so validation works correctly
            <Field
                name={name}
                component={Selector}
            />
        );
    }
}

ReactSelect.propTypes = {
    options: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
    })).isRequired,
};

export default withStyles(styles, { withTheme: true })(ReactSelect);


// WEBPACK FOOTER //
// ./src/helpers/ReactSelect.js